/**
 * A stated ratio beside the one the deal's own figures imply.
 *
 * Rule R3 (Derived-Value Conflict Rule, 22 Sep 2026): a ratio a document states
 * is kept as its own sourced value and never back-solves an input; Rule R4: the
 * variance between it and the derived one is shown for information, never
 * blocking. 45 Park Ave's header read "Value $44.9M · LTV 58.00%" after a
 * per-component valuation moved the value — the broker's 58% stood beside a
 * value that implies 69%, and nothing on screen said so.
 */
import { formatPct } from './format'

/** Points of LTV below which stated and derived are the same figure, rounded. */
const LTV_VARIANCE_POINTS = 0.5

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.replace(/[$,%\s]/g, ''))
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** LTV on the 0-100 scale, whichever scale it was stored on. */
function ltvPoints(value: number): number {
  return Math.abs(value) <= 1.5 ? value * 100 : value
}

/** The LTV the loan amount and the as-is value imply, in points, or null. */
export function impliedLtv(loanAmount: unknown, propertyValue: unknown): number | null {
  const loan = asNumber(loanAmount)
  const value = asNumber(propertyValue)
  if (loan == null || value == null || loan <= 0 || value <= 0) return null
  return (loan / value) * 100
}

/**
 * The LTV as the header shows it: the recorded figure, and — when the deal's
 * own loan and value imply a different one — that figure beside it, labelled.
 *
 * `served` is the implied LTV the server computed and judged against its one
 * tolerance (`/calculations` → `derived.ltv.fromInputs` / `matchesInputs`),
 * the same for every role (UI-03): a number is shown as the server gave it,
 * `null` says the server found no variance, and `undefined` — the server
 * returned nothing — leaves the browser's own division and its rounding
 * threshold as the fallback.
 */
export function ltvWithImplied(
  ltv: unknown,
  loanAmount: unknown,
  propertyValue: unknown,
  served?: number | null,
): string {
  const stated = asNumber(ltv)
  if (stated == null || stated === 0) return formatPct(ltv)
  if (served !== undefined) {
    return served == null ? formatPct(ltv) : `${formatPct(ltv)} · ${served.toFixed(1)}% derived`
  }
  const implied = impliedLtv(loanAmount, propertyValue)
  if (implied == null || Math.abs(ltvPoints(stated) - implied) < LTV_VARIANCE_POINTS) {
    return formatPct(ltv)
  }
  return `${formatPct(ltv)} · ${implied.toFixed(1)}% derived`
}
